import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  CreditCard,
  FileText,
  Loader2,
  ExternalLink,
  Check,
  Settings2,
  Plus,
  Pencil,
  X
} from 'lucide-react';

const BillingInvoice = () => {
  const { currentWorkspace, subscriptionInfo } = useWorkspace();
  const [activeTab, setActiveTab] = useState('billing');
  const [plans, setPlans] = useState([]);
  const [subscriptionCurrent, setSubscriptionCurrent] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [managePlans, setManagePlans] = useState([]);
  const [managePlansLoading, setManagePlansLoading] = useState(false);
  const [planModal, setPlanModal] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);

  const isWorkspace1Admin = Number(currentWorkspace?.id) === 1 && subscriptionInfo?.is_super_admin;

  const loadBilling = useCallback(async () => {
    setPlansLoading(true);
    try {
      const [plansRes, subRes] = await Promise.all([
        apiClient.get('/api/plans'),
        apiClient.get('/api/subscription/current')
      ]);
      if (plansRes.data?.success) setPlans(plansRes.data.plans || []);
      if (subRes.data?.success) setSubscriptionCurrent(subRes.data);
    } catch (e) {
      toast.error('Failed to load billing');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await apiClient.get('/api/subscription/invoices');
      if (res.data?.success) setInvoices(res.data.invoices || []);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  const loadManagePlans = useCallback(async () => {
    setManagePlansLoading(true);
    try {
      const res = await apiClient.get('/api/plans/manage');
      if (res.data?.success) setManagePlans(res.data.plans || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load plans');
    } finally {
      setManagePlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'billing') loadBilling();
    if (activeTab === 'invoices') loadInvoices();
    if (activeTab === 'plans' && isWorkspace1Admin) loadManagePlans();
  }, [activeTab, loadBilling, loadInvoices, loadManagePlans, isWorkspace1Admin]);

  useEffect(() => {
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const trialDaysLeft = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
    return diff > 0 ? diff : 0;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const x = typeof d === 'number' ? new Date(d * 1000) : new Date(d);
    return x.toLocaleDateString(undefined, { dateStyle: 'medium' });
  };

  const handleStartTrial = async (planId) => {
    try {
      setUpgradingPlanId(planId);
      const { data } = await apiClient.post('/api/subscription/create', { plan_id: planId });
      if (!data.subscription_id || !data.key) {
        toast.error(data.error || 'Payment not configured');
        return;
      }
      const options = {
        key: data.key,
        subscription_id: data.subscription_id,
        name: 'Leads Automater',
        handler: async (res) => {
          await apiClient.post('/api/subscription/verify', {
            razorpay_subscription_id: data.subscription_id
          });
          toast.success('Trial started successfully');
          loadBilling();
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to start trial');
    } finally {
      setUpgradingPlanId(null);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      setUpgradingPlanId(planId);
      const { data } = await apiClient.post('/api/subscription/create-order', { plan_id: planId });
      if (!data.order_id || !data.key) {
        toast.error(data.error || 'Payment not configured');
        return;
      }
      const options = {
        key: data.key,
        order_id: data.order_id,
        name: 'Leads Automater',
        handler: async (res) => {
          await apiClient.post('/api/subscription/verify-payment', {
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_signature: res.razorpay_signature
          });
          toast.success('Payment successful');
          loadBilling();
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to upgrade');
    } finally {
      setUpgradingPlanId(null);
    }
  };

  const handleSavePlan = async (payload) => {
    setPlanSaving(true);
    try {
      if (planModal?.id) {
        await apiClient.put(`/api/plans/${planModal.id}`, payload);
        toast.success('Plan updated');
      } else {
        await apiClient.post('/api/plans', payload);
        toast.success('Plan created');
      }
      setPlanModal(null);
      loadManagePlans();
      loadBilling();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const handleTogglePlanActive = async (plan) => {
    try {
      await apiClient.patch(`/api/plans/${plan.id}`, { is_active: !plan.is_active });
      toast.success(plan.is_active ? 'Plan deactivated' : 'Plan activated');
      loadManagePlans();
      loadBilling();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update plan');
    }
  };

  const isLifetimeSubscription =
    subscriptionInfo?.workspace_comped_lifetime === true ||
    subscriptionCurrent?.is_lifetime_plan === true ||
    subscriptionInfo?.is_lifetime_plan === true ||
    subscriptionCurrent?.plan?.slug === 'internal-lifetime';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Invoice</h1>
        <p className="text-gray-600">Manage your subscription and view invoices.</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('billing')}
              className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'billing'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CreditCard className="h-4 w-4" />
              Billing
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'invoices'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="h-4 w-4" />
              Invoices
            </button>
            {isWorkspace1Admin && (
              <button
                onClick={() => setActiveTab('plans')}
                className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${
                  activeTab === 'plans'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Settings2 className="h-4 w-4" />
                Plans
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'billing' && (
            <>
              {plansLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {subscriptionCurrent?.plan && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-700">Current plan: {subscriptionCurrent.plan.name}</p>
                      <p className="text-sm text-gray-500">
                        Workspaces: {subscriptionCurrent.workspace_count ?? 0} / {subscriptionCurrent.max_workspaces ?? 1}
                      </p>
                      {isLifetimeSubscription && (
                        <p className="text-sm text-blue-700 mt-1">Access does not expire — no renewal date.</p>
                      )}
                      {subscriptionCurrent.subscription?.status === 'trial' && (
                        <p className="text-sm text-blue-700 mt-1">
                          Trial active – {trialDaysLeft(subscriptionCurrent.subscription.trial_end)} day(s) left
                        </p>
                      )}
                      {!isLifetimeSubscription && subscriptionCurrent.subscription?.status === 'trial' && subscriptionCurrent.subscription?.trial_end && (
                        <p className="text-sm text-gray-500">Next billing: {formatDate(subscriptionCurrent.subscription.trial_end)}</p>
                      )}
                      {!isLifetimeSubscription && subscriptionCurrent.subscription?.status === 'active' && subscriptionCurrent.subscription?.current_period_end && (
                        <p className="text-sm text-gray-500">Next billing: {formatDate(subscriptionCurrent.subscription.current_period_end)}</p>
                      )}
                      {(subscriptionCurrent.subscription_expired || subscriptionCurrent.subscription?.status === 'expired') && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800 font-medium">Your subscription has expired.</p>
                          <Link to="/settings?tab=plans" className="text-sm text-amber-700 font-medium underline mt-1 inline-block">
                            Renew in Settings
                          </Link>
                        </div>
                      )}
                      {subscriptionCurrent.subscription?.status === 'failed' && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-800 font-medium">Payment failed. Update your payment method.</p>
                          <Link to="/settings?tab=plans" className="text-sm text-red-700 font-medium underline mt-1 inline-block">
                            Update in Settings
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {!isLifetimeSubscription && (
                  <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Upgrade your plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {plans.filter((p) => p.price_paise > 0).map((plan) => {
                      const isCurrent = subscriptionCurrent?.plan?.id === plan.id;
                      return (
                        <div
                          key={plan.id}
                          className={`rounded-lg p-4 border ${
                            isCurrent ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200'
                          }`}
                        >
                          <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                          <p className="text-2xl font-bold text-gray-900 mt-1">
                            ₹{(plan.price_paise / 100).toFixed(0)}
                            {plan.billing_interval && <span className="text-sm font-normal text-gray-500">/{plan.billing_interval}</span>}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">{plan.max_workspaces} workspace(s)</p>
                          {!isCurrent && (
                            <div className="mt-3 flex flex-col gap-2">
                              <button
                                onClick={() => handleStartTrial(plan.id)}
                                disabled={upgradingPlanId === plan.id}
                                className="w-full py-2 px-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              >
                                {upgradingPlanId === plan.id ? 'Opening...' : 'Start Free Trial'}
                              </button>
                              <button
                                onClick={() => handleUpgrade(plan.id)}
                                disabled={upgradingPlanId === plan.id}
                                className="w-full py-2 px-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                              >
                                Pay once (no trial)
                              </button>
                            </div>
                          )}
                          {isCurrent && (
                            <div className="mt-2 flex items-center text-blue-600 text-sm font-medium">
                              <Check className="w-4 h-4 mr-1" />
                              Current plan
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    <Link to="/settings?tab=plans" className="text-blue-600 hover:underline">
                      Manage subscription & cancel options in Settings
                    </Link>
                  </p>
                </>
              )}
                </>
              )}
            </>
          )}

          {activeTab === 'plans' && isWorkspace1Admin && (
            <>
              {managePlansLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Manage Plans</h3>
                    <button
                      onClick={() => setPlanModal({})}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                      Add Plan
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workspaces</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {managePlans.map((plan) => (
                          <tr key={plan.id} className={`hover:bg-gray-50 ${!plan.is_active ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono">{plan.slug}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              ₹{(plan.price_paise / 100).toFixed(0)}
                              {plan.billing_interval && <span className="text-gray-500">/{plan.billing_interval}</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{plan.max_workspaces}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${plan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {plan.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => setPlanModal(plan)}
                                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleTogglePlanActive(plan)}
                                  className={`p-2 rounded-lg ${plan.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                                  title={plan.is_active ? 'Deactivate' : 'Activate'}
                                >
                                  {plan.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    Changing plan price creates a new Razorpay plan. Existing subscriptions keep the old plan until they renew.
                  </p>
                </>
              )}
            </>
          )}

          {activeTab === 'invoices' && (
            <>
              {invoicesLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No invoices yet</p>
                  <p className="text-sm text-gray-400 mt-1">Invoices will appear here after you subscribe to a paid plan.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issued</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Link</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 font-mono">{inv.id}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                              inv.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {inv.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            ₹{((inv.amount || 0) / 100).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(inv.issued_at)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{inv.paid_at ? formatDate(inv.paid_at) : '—'}</td>
                          <td className="px-4 py-3">
                            {inv.short_url && (
                              <a
                                href={inv.short_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-1 text-sm"
                              >
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {planModal !== null && (
        <PlanFormModal
          plan={planModal}
          onClose={() => setPlanModal(null)}
          onSave={handleSavePlan}
          saving={planSaving}
        />
      )}
    </div>
  );
};

function PlanFormModal({ plan, onClose, onSave, saving }) {
  const [name, setName] = useState(plan.name || '');
  const [slug, setSlug] = useState(plan.slug || '');
  const [maxWorkspaces, setMaxWorkspaces] = useState(plan.max_workspaces ?? 1);
  const [pricePaise, setPricePaise] = useState(plan.price_paise != null ? String(plan.price_paise / 100) : '');
  const [billingInterval, setBillingInterval] = useState(plan.billing_interval || 'monthly');
  const isEdit = !!plan.id;

  useEffect(() => {
    setName(plan.name || '');
    setSlug(plan.slug || '');
    setMaxWorkspaces(plan.max_workspaces ?? 1);
    setPricePaise(plan.price_paise != null ? String(plan.price_paise / 100) : '');
    setBillingInterval(plan.billing_interval || 'monthly');
  }, [plan.id, plan.name, plan.slug, plan.max_workspaces, plan.price_paise, plan.billing_interval]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const price = Math.round(parseFloat(pricePaise || 0) * 100);
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    const slugVal = slug.trim() || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!slugVal) {
      toast.error('Slug is required');
      return;
    }
    onSave({
      name: name.trim(),
      slug: slugVal,
      max_workspaces: Math.max(1, maxWorkspaces),
      price_paise: price,
      billing_interval: price > 0 ? billingInterval : null
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Plan' : 'Create Plan'}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Pro"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              placeholder="e.g. pro"
            />
            <p className="text-xs text-gray-500 mt-1">Lowercase, hyphens only. Auto-generated from name if empty.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Workspaces</label>
            <input
              type="number"
              min="1"
              value={maxWorkspaces}
              onChange={(e) => setMaxWorkspaces(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price (INR)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={pricePaise}
              onChange={(e) => setPricePaise(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0 for free plan"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Interval</label>
            <select
              value={billingInterval}
              onChange={(e) => setBillingInterval(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BillingInvoice;
